import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, UserRound } from "lucide-react";
import { Chuckle, Drip, Shiver, Bob } from "./Characters";
import { PinkButton } from "./Buttons";

const LINES = ["Make us", "feel something."];
const ease = [0.22, 1, 0.36, 1];

const MaskLine = ({ text, i, reduce }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={reduce ? false : { y: "110%", rotate: 3 }}
      animate={{ y: "0%", rotate: 0 }}
      transition={{ duration: 1.1, delay: 0.15 + i * 0.14, ease }}
    >
      {i === 1 ? <>feel <span className="text-sun">something</span>.</> : text}
    </motion.span>
  </span>
);

const HeroCast = ({ scrollYProgress, reduce }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-1, 1], [8, -8]), { stiffness: 80, damping: 14 });
  const ry = useSpring(useTransform(mx, [-1, 1], [-12, 12]), { stiffness: 80, damping: 14 });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -60]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -130]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -90]);
  const onMove = (e) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width) * 2 - 1);
    my.set(((e.clientY - r.top) / r.height) * 2 - 1);
  };
  const cast = [
    { C: Chuckle, y: y1, d: 0, cls: "w-[38%] max-w-[300px] -mr-[4%] z-10", z: 40 },
    { C: Drip, y: y2, d: 0.7, cls: "w-[34%] max-w-[270px] z-20 mb-[4%]", z: 90 },
    { C: Shiver, y: y3, d: 1.3, cls: "w-[36%] max-w-[290px] -ml-[4%] z-10", z: 60 },
  ];
  return (
    <div onPointerMove={onMove} onPointerLeave={() => { mx.set(0); my.set(0); }} className="relative mt-14 sm:mt-6 [perspective:1100px]" data-testid="hero-characters-container">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative mx-auto flex max-w-5xl items-end justify-center translate-y-[22%]">
        {cast.map(({ C, y, d, cls, z }, i) => (
          <motion.div key={i} style={{ y, translateZ: z }} className={cls}>
            <motion.div initial={reduce ? false : { y: "60%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.6 + i * 0.12, ease }}>
              <Bob delay={d} className="w-full"><C className="h-auto w-full drop-shadow-[0_24px_40px_rgba(0,0,0,0.35)]" /></Bob>
            </motion.div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

export const Hero = ({ onOpenSignup }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 80]);
  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-ink pt-28 text-paper sm:pt-36">
      <motion.div style={{ y: textY }} className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-10">
        <motion.p initial={reduce ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-1.5 font-body text-sm font-semibold text-paper/90">
          <span className="h-2 w-2 rounded-full bg-pop" /> convey: 2 Minute Film Awards
        </motion.p>
        <h1 data-testid="hero-headline" className="font-head text-[3.1rem] leading-[0.95] tracking-tight sm:text-7xl lg:text-[8.5rem]">
          {LINES.map((t, i) => <MaskLine key={t} text={t} i={i} reduce={reduce} />)}
        </h1>
        <motion.div initial={reduce ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.55, ease }}
          className="mt-8 flex max-w-2xl flex-col gap-7 lg:mt-10">
          <p data-testid="hero-subline" className="font-body text-lg leading-relaxed text-paper/90 sm:text-xl">
            Two minutes. Three feelings. Free to enter. AI and non-AI welcome.
          </p>
          <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:gap-8">
            <PinkButton data-testid="hero-cta-get-prep-pack" onClick={() => onOpenSignup("pack")}>Receive the preparation pack <ArrowRight size={18} /></PinkButton>
            <Link to="/login" data-testid="hero-link-filmmaker-login" className="link-underline inline-flex items-center gap-1.5 rounded font-body text-lg font-semibold text-paper">
              <UserRound size={18} /> Filmmaker login
            </Link>
          </div>
        </motion.div>
      </motion.div>
      <HeroCast scrollYProgress={scrollYProgress} reduce={reduce} />
      <div className="brand-strip absolute inset-x-0 bottom-0 z-30 h-2" aria-hidden="true" />
    </section>
  );
};
